// Pure canvas draw helpers for Video Studio Phase 4 (branding, graphics).
// All functions draw onto a 2D context sized to the export dimensions.

export function hexToRgba(hex, a = 1) {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return `rgba(79,70,229,${a})`;
  return `rgba(${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)},${a})`;
}

// Loads an image untainted (cross-origin URLs are fetched to a blob first so
// canvas.captureStream() doesn't throw a SecurityError).
export async function loadImage(url) {
  let src = url;
  if (/^https?:/i.test(url)) {
    const r = await fetch(url);
    src = URL.createObjectURL(await r.blob());
  }
  const img = new Image();
  img.crossOrigin = 'anonymous';
  await new Promise((res, rej) => { img.onload = res; img.onerror = () => rej(new Error('img')); img.src = src; });
  return img;
}

export function drawLogo(ctx, img, cfg, W, H) {
  if (!img || !cfg?.enabled) return;
  const sizePct = cfg.size ?? 0.18;
  const margin = Math.round(W * 0.03);
  const iw = Math.round(W * sizePct);
  const ih = Math.round(iw * ((img.height / img.width) || 1));
  const pos = cfg.position || 'tr';
  let x = margin; let y = margin;
  if (pos.includes('r')) x = W - iw - margin;
  if (pos.includes('b')) y = H - ih - margin;
  ctx.save();
  ctx.globalAlpha = cfg.opacity ?? 0.9;
  ctx.drawImage(img, x, y, iw, ih);
  ctx.restore();
}

export function drawProgressBar(ctx, p, color, W, H) {
  const bh = Math.max(4, Math.round(H * 0.012));
  ctx.save();
  ctx.fillStyle = 'rgba(255,255,255,0.28)';
  ctx.fillRect(0, 0, W, bh);
  ctx.fillStyle = color || '#4f46e5';
  ctx.fillRect(0, 0, Math.round(W * Math.min(1, Math.max(0, p))), bh);
  ctx.restore();
}

export function drawLowerThird(ctx, lt, W, H) {
  const title = (lt.title || '').trim();
  const sub = (lt.subtitle || '').trim();
  if (!title && !sub) return;
  const pad = Math.round(W * 0.04);
  const y = Math.round(H * 0.70);
  const barH = Math.round(H * (sub ? 0.15 : 0.10));
  ctx.save();
  ctx.fillStyle = hexToRgba(lt.color || '#4f46e5', 0.88);
  ctx.fillRect(0, y, Math.round(W * 0.82), barH);
  ctx.fillStyle = lt.textColor || '#ffffff';
  ctx.textBaseline = 'top';
  ctx.textAlign = 'left';
  ctx.font = `700 ${Math.round(H * 0.045)}px Inter, Arial, sans-serif`;
  ctx.fillText(title, pad, y + Math.round(pad * 0.5));
  if (sub) {
    ctx.font = `500 ${Math.round(H * 0.030)}px Inter, Arial, sans-serif`;
    ctx.fillText(sub, pad, y + Math.round(pad * 0.5) + Math.round(H * 0.055));
  }
  ctx.restore();
}

export function drawOutro(ctx, outro, img, W, H) {
  ctx.save();
  if (outro.mode === 'image' && img) {
    const s = Math.max(W / img.width, H / img.height);
    const iw = img.width * s; const ih = img.height * s;
    ctx.drawImage(img, (W - iw) / 2, (H - ih) / 2, iw, ih);
  } else {
    ctx.fillStyle = outro.bgColor || '#0f172a';
    ctx.fillRect(0, 0, W, H);
    const lines = (outro.lines || []).filter((l) => l && l.trim());
    ctx.fillStyle = outro.textColor || '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const cy = H / 2 - ((lines.length - 1) * Math.round(H * 0.05));
    lines.forEach((ln, i) => {
      ctx.font = `${i === 0 ? '800' : '500'} ${Math.round(H * (i === 0 ? 0.06 : 0.04))}px Inter, Arial, sans-serif`;
      ctx.fillText(ln, W / 2, cy + i * Math.round(H * 0.10));
    });
  }
  ctx.restore();
}
